import cx from 'classnames';
import {isFunction} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, IconProps} from 'components';
import {AnyParticipant} from 'interfaces';
import {ParticipantRole} from 'types';
import {sessionUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

type Props = {
    participant: AnyParticipant;
    icon?: boolean;
    iconClassName?: string;
    nameOnly?: boolean;
    nameStyle?: 'base' | 'h3-bold-dimmed';
    localParticipant?: AnyParticipant;
    local?: boolean;
    canUpdate?: boolean;
    onClick?: () => void;
    onUpdate?: (participant?: AnyParticipant) => void;
}

export const ParticipantName: React.FC<Props> = ({
    participant, localParticipant, local: localProp, icon = true, iconClassName, nameOnly,
    nameStyle = 'base', canUpdate, onUpdate, onClick
}: Props) => {
    const {t} = useTranslation();
    const iconProps: Partial<IconProps> = useMemo(() => {
        if (nameStyle === 'h3-bold-dimmed') {
            return {color: 'default-dimmed', size: 'lg', className: 'tw-mr-2'};
        }
        return {className: iconClassName || 'tw-mr-1'};
    }, [iconClassName, nameStyle]);

    const nameClasses = useMemo(() => cx({
        'tw-text-default-dimmed tw-ts-h5-bold': nameStyle === 'h3-bold-dimmed',
        'tw-cursor-pointer hover:tw-text-primary': isFunction(onClick),
        'tw-text-default-dimmed': participant.removed,
    }), [nameStyle, onClick, participant]);

    const handleUpdate = useCallback((event) => {
        event?.stopPropagation();
        onUpdate(participant);
    }, [participant, onUpdate]);

    const renderUpdateIcon = useCallback(() => {
        if (canUpdate) {
            return (
                <Icon
                    className='tw-ml-2'
                    button
                    group
                    name='edit'
                    color='primary'
                    size='sm'
                    onClick={handleUpdate}
                />
            );
        }
    }, [canUpdate, handleUpdate]);

    if (participant?.role === ParticipantRole.Agent) {
        const local = localProp || participant.id === localParticipant?.id;
        return (
            <div className='tw-inline-flex tw-items-center'>
                {icon && !nameOnly && <Icon name='agent-participant' {...iconProps} />}
                <div className={nameClasses} onClick={onClick}>
                    {sessionUtils.getParticipantNameWithMe(t, participant.data, local && !nameOnly)}
                </div>
                {renderUpdateIcon()}
            </div>
        );
    } else if (participant?.role === ParticipantRole.Assistant) {
        const local = localProp || participant.id === localParticipant?.id;
        return (
            <div className='tw-inline-flex tw-items-center'>
                {icon && !nameOnly && <Icon name='assistant-participant' {...iconProps} />}
                <div className={nameClasses} onClick={onClick}>
                    {sessionUtils.getParticipantNameWithMe(t, participant.data, local && !nameOnly)}
                </div>
                {renderUpdateIcon()}
            </div>
        );
    } else if (participant?.role === ParticipantRole.Client) {
        return (
            <div className='tw-inline-flex tw-items-center'>
                {icon && !nameOnly && <Icon name='client-participant' {...iconProps} />}
                <div className={nameClasses} onClick={onClick}>
                    {getParticipantName(participant.data.userInfo, participant.data.userInfo?.id)}
                </div>
                {renderUpdateIcon()}
            </div>
        );
    } else if (participant?.role === ParticipantRole.Guest) {
        return (
            <div className='tw-inline-flex tw-items-center'>
                {icon && !nameOnly && <Icon name='guest-participant' {...iconProps} />}
                <div className={nameClasses} onClick={onClick}>
                    {getParticipantName(participant.data.userInfo, participant.data.userInfo?.id)}
                </div>
                {renderUpdateIcon()}
            </div>
        );
    }
    return null;
};
